// DEPENDENCIES
import * as Sentry from "@sentry/react";
import { FormikProps, FormikValues } from "formik";

// TYPES
import { ComponentStep } from "../../types";
import { AppDispatch, RootState } from "../../app/store";

// UTILS
import { isTokenActive } from "../../app/utils";
import {
  physicalEmailVerificationAuthorize,
  physicalEmailVerificationSuccess,
} from "../../utils";

// COMPONENTS
import TokenStep from "../../components/TokenStep";

import styles from "../../components/SupplementaryComponentInfoRenderer/SupplementaryComponentInfoRenderer.module.scss";

const tokenStep = ({
  step,
  onAuthorize: handleAuthorize,
  onSuccess: handleSuccess,
  oldAuth,
}: {
  step: number;
  onAuthorize?: (
    draftUUID: string,
    dispatch: AppDispatch,
    formik: FormikProps<FormikValues>,
    state: RootState,
  ) => Promise<void>;
  onSuccess?: (
    dispatch: AppDispatch,
    state: RootState,
    formik: FormikProps<FormikValues>,
  ) => void | Promise<void>;
  oldAuth?: boolean;
}): Omit<ComponentStep, "step"> => ({
  label: "token",
  name: "Mail",
  component: (dispatch, state, formik) => {
    if (!state || !dispatch || !formik)
      throw new Error("Error: state, dispatch or formik not found");

    const onAuthorize = async (draftUUID?: string) => {
      try {
        if (!draftUUID || !state || !formik)
          throw new Error("DraftUUID or state not found");

        if (handleAuthorize) {
          return await handleAuthorize(draftUUID, dispatch, formik, state);
        }

        await physicalEmailVerificationAuthorize(
          draftUUID,
          dispatch,
          formik,
          state,
        );
      } catch (error) {
        console.log(error, "Error on authorize");
        Sentry.captureException(error);
      }
    };

    const onSuccess = async () => {
      if (handleSuccess) {
        return await handleSuccess(dispatch, state, formik);
      }

      await physicalEmailVerificationSuccess(dispatch, formik, step);
    };

    return (
      <TokenStep
        previousStep={step - 1}
        onSuccess={onSuccess}
        onAuthorize={onAuthorize}
        formik={formik}
        oldAuth={oldAuth}
      />
    );
  },
  disableBackNavigation: true,
  supplementaryComponentInfo: {
    show: (state) => {
      const token = state?.authentication?.token;
      return !!(token && !isTokenActive({ token }));
    },
    position: "top",
    component: () => {
      return (
        <p className={styles.emailValidation}>
          Validá tu dirección de mail para retomar el proceso de registro
        </p>
      );
    },
  },
});

export default tokenStep;
